import React, { useState, useMemo } from "react";
import { T, fmt } from "./theme";
import { SectionHeader, Card, Input, Sel, Btn, Note, Badge } from "./ui";
import { Icon } from "./Icons";
import { CATALOGO } from "./data";

// ═══════════════════════════════════════════════════════════════════════
// SIMULADOR DE AMBIENTES — gera sugestão textual baseada em área e orçamento
// ═══════════════════════════════════════════════════════════════════════

const SEGMENTOS = [
  { id: "salao", label: "Salão de Beleza" },
  { id: "barbearia", label: "Barbearia" },
  { id: "clinica", label: "Clínica Estética" },
];

export default function Simulador() {
  const [segmento, setSegmento] = useState("salao");
  const [area, setArea] = useState(30);
  const [orcamento, setOrcamento] = useState(25000);
  const [cliente, setCliente] = useState("");

  const sugestao = useMemo(() => {
    const catalogo = CATALOGO[segmento] || [];
    // algoritmo: prioriza itens "essenciais" e preenche respeitando área e orçamento
    const ordenado = [...catalogo].sort((a, b) => b.preco / b.area - a.preco / a.area);
    const selecionados = [];
    let areaUsada = 0;
    let custoTotal = 0;

    for (const item of ordenado) {
      let qtd = 0;
      while (areaUsada + item.area <= area * 0.7 && custoTotal + item.preco <= orcamento) {
        areaUsada += item.area;
        custoTotal += item.preco;
        qtd++;
        if (qtd >= 5) break; // limite prudente
      }
      if (qtd > 0) selecionados.push({ ...item, qtd });
    }

    return { itens: selecionados, areaUsada, custoTotal, aproveitamento: (areaUsada / area) * 100 };
  }, [segmento, area, orcamento]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Simulador de Ambientes"
        subtitle="Gere uma proposta de mobiliário baseada em área e orçamento do cliente"
        icon={<Icon.room size={24} />}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <Card>
          <Sel label="Segmento do cliente" value={segmento} onChange={e => setSegmento(e.target.value)}>
            {SEGMENTOS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </Sel>
        </Card>
        <Card>
          <Input label="Área total (m²)" type="number" value={area} onChange={e => setArea(Number(e.target.value))} hint="Área útil do ambiente" />
        </Card>
        <Card>
          <Input label="Orçamento do cliente (R$)" type="number" value={orcamento} onChange={e => setOrcamento(Number(e.target.value))} hint="Valor disponível para mobiliário" />
        </Card>
        <Card>
          <Input label="Cliente / Projeto" value={cliente} onChange={e => setCliente(e.target.value)} hint="Opcional, para identificar a simulação" />
        </Card>
      </div>

      {/* Resultado */}
      <Card soft>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 14, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: T.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>
              Sugestão gerada
            </div>
            <div className="serif" style={{ fontSize: 22, fontWeight: 600, color: T.brand, marginTop: 4 }}>
              {cliente || "Proposta"} · {SEGMENTOS.find(s => s.id === segmento).label}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Badge text={`${area} m² total`} tone="info" />
            <Badge text={`${sugestao.areaUsada.toFixed(1)} m² ocupado`} tone="ok" />
            <Badge text={`${sugestao.aproveitamento.toFixed(0)}% aproveitamento`} tone="brand" />
          </div>
        </div>

        {sugestao.itens.length === 0 ? (
          <Note tone="warn" icon={<Icon.alert size={14} />}>
            Não foi possível montar uma proposta com essa área e orçamento. Tente aumentar algum dos dois.
          </Note>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  <th style={{ textAlign: "left", padding: "10px 8px", fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Item</th>
                  <th style={{ textAlign: "center", padding: "10px 8px", fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Qtd</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Área</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Unitário</th>
                  <th style={{ textAlign: "right", padding: "10px 8px", fontSize: 10, color: T.textSub, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {sugestao.itens.map((it, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: "10px 8px", color: T.text }}>{it.item}</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", color: T.textSec }}>{it.qtd}</td>
                    <td style={{ padding: "10px 8px", textAlign: "right", color: T.textSub }}>{(it.area * it.qtd).toFixed(1)} m²</td>
                    <td style={{ padding: "10px 8px", textAlign: "right", color: T.textSub }}>{fmt.brl(it.preco)}</td>
                    <td style={{ padding: "10px 8px", textAlign: "right", color: T.brand, fontWeight: 600 }}>{fmt.brl(it.preco * it.qtd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginTop: 16, paddingTop: 16, borderTop: `2px solid ${T.brand}44`,
            }}>
              <div>
                <div style={{ fontSize: 11, color: T.textSub }}>Total estimado</div>
                <div className="serif" style={{ fontSize: 28, fontWeight: 600, color: T.brand, letterSpacing: -0.5 }}>
                  {fmt.brl(sugestao.custoTotal)}
                </div>
              </div>
              <div style={{ textAlign: "right", fontSize: 12, color: T.textSub }}>
                <div>Orçamento cliente: <b>{fmt.brl(orcamento)}</b></div>
                <div style={{ color: orcamento - sugestao.custoTotal >= 0 ? T.ok : T.err, fontWeight: 600, marginTop: 2 }}>
                  {orcamento - sugestao.custoTotal >= 0 ? "Sobra" : "Excede"}: {fmt.brl(Math.abs(orcamento - sugestao.custoTotal))}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 14, fontSize: 11, color: T.textDim, fontStyle: "italic" }}>
              * Valores do catálogo Bellanor. Simulação para referência comercial — proposta oficial deve ser gerada no Prospero (integrado ao Wix).
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
